// ✅ src/components/screens/CartScreen.js (FULL - UPDATED PER STORE + QUERY FIX)
// ✅ Cart is per shop/storeId using localStorage key: cart_items_v1:${storeId}
// ✅ storeId is read from:
//    1) navigation state: location.state.storeId
//    2) URL query: /cart?store=<storeId> OR /cart?storeId=<storeId>
//    3) fallback: auto-pick first store cart found in localStorage
// ✅ If fallback finds a storeId, it auto-redirects to /cart?store=<storeId>

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

/* ---------------- helpers ---------------- */
function safe(v) {
  return String(v ?? "").trim();
}
function money(n, cur = "GHS") {
  const num = Number(n || 0);
  if (Number.isNaN(num)) return `${cur} 0`;
  return `${cur} ${num.toLocaleString()}`;
}
function getPriceValue(p) {
  const candidates = [
    p?.price,
    p?.amount,
    p?.unitPrice,
    p?.priceGHS,
    p?.priceValue,
    p?.salePrice,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  const zero = Number(p?.price);
  if (!Number.isNaN(zero)) return zero;
  return 0;
}

/* ---------------- cart (PER STORE) ---------------- */
function cartKey(storeId) {
  return `cart_items_v1:${safe(storeId)}`;
}
function loadCart(storeId) {
  try {
    if (!safe(storeId)) return [];
    const raw = localStorage.getItem(cartKey(storeId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function saveCart(storeId, items) {
  try {
    if (!safe(storeId)) return;
    localStorage.setItem(cartKey(storeId), JSON.stringify(items || []));
  } catch {}
}

/* ---------------- fallback: find any existing store cart ---------------- */
function findAnyStoreIdFromCartKeys() {
  try {
    const keys = Object.keys(localStorage);
    const prefix = "cart_items_v1:";
    const cartKeys = keys.filter((k) => k.startsWith(prefix));

    for (const k of cartKeys) {
      const raw = localStorage.getItem(k);
      const arr = raw ? JSON.parse(raw) : [];
      if (Array.isArray(arr) && arr.length > 0) return k.replace(prefix, "");
    }
    return "";
  } catch {
    return "";
  }
}

export default function CartScreen({ goBack }) {
  const navigate = useNavigate();
  const location = useLocation();

  /** @type {[import("firebase/auth").User|null, Function]} */
  const [user, setUser] = useState(() => auth.currentUser);

  // ✅ storeId from state OR query (?store= or ?storeId=) OR fallback
  const storeId = useMemo(() => {
    const fromState = safe(location?.state?.storeId);
    if (fromState) return fromState;

    try {
      const sp = new URLSearchParams(location?.search || "");
      const q = safe(sp.get("store")) || safe(sp.get("storeId")); // ✅ FIX
      if (q) return q;
    } catch {}

    return findAnyStoreIdFromCartKeys();
  }, [location?.state, location?.search]);

  // ✅ if we had to fallback, auto-redirect to /cart?store=...
  useEffect(() => {
    const fromState = safe(location?.state?.storeId);
    let fromQuery = "";
    try {
      const sp = new URLSearchParams(location?.search || "");
      fromQuery = safe(sp.get("store")) || safe(sp.get("storeId"));
    } catch {}

    if (safe(storeId) && !fromState && !fromQuery) {
      navigate(`/cart?store=${encodeURIComponent(storeId)}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const [items, setItems] = useState(() => loadCart(storeId)); // [{productId, qty}]
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const [productMap, setProductMap] = useState({});

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, []);

  // ✅ when storeId changes, reload that cart
  useEffect(() => {
    setItems(loadCart(storeId));
    setProductMap({});
  }, [storeId]);

  // ✅ save per-store
  useEffect(() => {
    saveCart(storeId, items);
  }, [storeId, items]);

  // ✅ sync across tabs for THIS store only
  useEffect(() => {
    const onStorage = (e) => {
      if (!safe(storeId)) return;
      if (e?.key === cartKey(storeId)) setItems(loadCart(storeId));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storeId]);

  // Load products
  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setMsg("");

      try {
        if (!safe(storeId)) {
          if (!alive) return;
          setProductMap({});
          setLoading(false);
          setMsg("Missing store. Please open cart from a store page.");
          return;
        }

        const ids = items.map((x) => safe(x?.productId)).filter(Boolean);

        if (ids.length === 0) {
          if (!alive) return;
          setProductMap({});
          setLoading(false);
          return;
        }

        const results = {};
        for (const id of ids) {
          try {
            const snap = await getDoc(doc(db, "products", id));
            if (snap.exists()) results[id] = { id, ...(snap.data() || {}) };
          } catch {}
        }

        if (!alive) return;
        setProductMap(results);
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setMsg(e?.message || "Failed to load cart.");
        setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [items, storeId]);

  const rows = useMemo(() => {
    return items
      .map((it) => {
        const pid = safe(it?.productId);
        const qty = Math.max(1, Math.min(99, Number(it?.qty || 1)));
        const p = productMap?.[pid] || null;
        return { productId: pid, qty, product: p };
      })
      .filter((r) => r.productId);
  }, [items, productMap]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let currency = "GHS";

    rows.forEach((r) => {
      const p = r.product;
      const cur = safe(p?.currency) || "GHS";
      currency = cur || currency;

      const price = getPriceValue(p);
      subtotal += Number(price || 0) * Number(r.qty || 1);
    });

    return { subtotal, currency };
  }, [rows]);

  const setQty = (productId, nextQty) => {
    const qty = Math.max(1, Math.min(99, Number(nextQty || 1)));
    setItems((prev) =>
      prev.map((x) =>
        safe(x?.productId) === String(productId) ? { ...x, qty } : x
      )
    );
  };

  const removeItem = (productId) => {
    setItems((prev) =>
      prev.filter((x) => safe(x?.productId) !== String(productId))
    );
  };

  const clearCart = () => setItems([]);

  const goCheckout = () => {
    if (!safe(storeId)) {
      setMsg("Missing store. Please open cart from a store page.");
      return;
    }

    if (!user) {
      navigate("/login", { state: { next: `/cart?store=${storeId}` } });
      return;
    }

    navigate("/checkout", { state: { storeId } });
  };

  return (
    <div className="cart">
      <style>{css}</style>

      <div className="cartTop">
        <button
          className="backBtn"
          type="button"
          onClick={goBack || (() => navigate(-1))}
        >
          ←
        </button>

        <div>
          <div className="h1">Your Cart</div>
          <div className="sub">
            {rows.length} item{rows.length === 1 ? "" : "s"}
            {safe(storeId) ? <span className="storeTag"> • store</span> : null}
          </div>
        </div>

        <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
          <button
            className="ghostBtn"
            type="button"
            onClick={clearCart}
            disabled={rows.length === 0}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="wrap">
        {loading ? (
          <div className="card">Loading…</div>
        ) : msg ? (
          <div className="card err">{msg}</div>
        ) : rows.length === 0 ? (
          <div className="card">
            <div className="emptyTitle">Your cart is empty 🧺</div>
            <div className="emptySub">Go back and add products to your cart.</div>
            <button
              className="primaryBtn"
              type="button"
              onClick={() => navigate("/browse")}
            >
              Browse stores
            </button>
          </div>
        ) : (
          <>
            <div className="list">
              {rows.map((r) => {
                const p = r.product;
                const title = safe(p?.title) || "Product";
                const img =
                  safe(p?.thumbnailUrl) ||
                  safe(p?.imageUrl) ||
                  safe(p?.mediaUrls?.[0]) ||
                  "";
                const cur = safe(p?.currency) || totals.currency || "GHS";
                const unit = getPriceValue(p);
                const line = Number(unit || 0) * Number(r.qty || 1);

                return (
                  <div className="row" key={r.productId}>
                    <div className="img">
                      {img ? <img src={img} alt={title} /> : <div className="ph">📦</div>}
                    </div>

                    <div className="mid">
                      <div className="name">{title}</div>
                      <div className="priceLine">
                        <span className="unit">{money(unit, cur)}</span>
                        <span className="dot">•</span>
                        <span className="line">{money(line, cur)}</span>
                      </div>

                      <div className="qty">
                        <button
                          className="qtyBtn"
                          type="button"
                          onClick={() => setQty(r.productId, r.qty - 1)}
                        >
                          −
                        </button>
                        <div className="qtyNum">{r.qty}</div>
                        <button
                          className="qtyBtn"
                          type="button"
                          onClick={() => setQty(r.productId, r.qty + 1)}
                        >
                          +
                        </button>

                        <button
                          className="linkBtn"
                          type="button"
                          onClick={() => navigate(`/p/${r.productId}`)}
                        >
                          View
                        </button>

                        <button
                          className="dangerBtn"
                          type="button"
                          onClick={() => removeItem(r.productId)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="summary">
              <div className="sumRow">
                <div className="sumLabel">Subtotal</div>
                <div className="sumVal">{money(totals.subtotal, totals.currency)}</div>
              </div>

              <button className="primaryBtn" type="button" onClick={goCheckout}>
                Checkout
              </button>

              <div className="note">Delivery/payment details happen at checkout.</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const css = `
.cart{
  min-height:100vh;
  background:#f3f5f8;
  color:#0f172a;
  font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
}
.cartTop{
  max-width:980px;
  margin:0 auto;
  padding:14px 14px 10px;
  display:flex;
  align-items:center;
  gap:12px;
}
.backBtn{
  width:42px;height:42px;border-radius:14px;
  border:1px solid #e5e7eb;background:#fff;
  box-shadow:0 10px 20px rgba(15,23,42,.06);
  cursor:pointer;font-weight:1200;
}
.h1{font-size:20px;font-weight:1100;letter-spacing:-.02em}
.sub{font-size:13px;color:#64748b;font-weight:900;margin-top:2px}
.storeTag{color:#94a3b8;font-weight:900}

.wrap{
  max-width:980px;
  margin:0 auto;
  padding:0 14px 30px;
  display:grid;
  gap:12px;
}
.card{
  background:#fff;border:1px solid #e6e9ef;border-radius:16px;
  padding:14px;box-shadow:0 10px 22px rgba(15,23,42,.06);
}
.card.err{border-color:#fecaca;color:#991b1b}
.emptyTitle{font-size:18px;font-weight:1100}
.emptySub{margin-top:6px;color:#64748b;font-weight:800}

.list{display:grid;gap:10px}
.row{
  background:#fff;border:1px solid #e6e9ef;border-radius:16px;
  padding:10px;display:flex;gap:10px;align-items:flex-start;
  box-shadow:0 10px 22px rgba(15,23,42,.06);
}
.img{
  width:90px;height:90px;border-radius:14px;overflow:hidden;
  background:#f1f5f9;display:grid;place-items:center;flex:0 0 auto;
}
.img img{width:100%;height:100%;object-fit:cover}
.ph{font-size:34px;opacity:.8}

.mid{flex:1;display:grid;gap:8px}
.name{font-weight:1100;line-height:1.2}
.priceLine{display:flex;gap:8px;align-items:center;color:#0b1220;font-weight:1000}
.unit{color:#334155;font-weight:900}
.dot{color:#94a3b8}
.line{color:#0b1220}

.qty{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.qtyBtn{
  width:38px;height:38px;border-radius:12px;
  border:1px solid #e5e7eb;background:#fff;
  cursor:pointer;font-weight:1200;
}
.qtyNum{
  min-width:40px;text-align:center;
  font-weight:1100;color:#0b1220;
}
.linkBtn{
  border:1px solid #e5e7eb;background:#fff;
  border-radius:12px;padding:9px 12px;
  cursor:pointer;font-weight:1000;
}
.dangerBtn{
  border:1px solid rgba(239,68,68,.25);
  background:rgba(239,68,68,.08);
  color:#991b1b;
  border-radius:12px;padding:9px 12px;
  cursor:pointer;font-weight:1000;
}

.summary{
  background:#fff;border:1px solid #e6e9ef;border-radius:16px;
  padding:12px 14px;box-shadow:0 10px 22px rgba(15,23,42,.06);
  display:grid;gap:10px;
}
.sumRow{display:flex;justify-content:space-between;align-items:center}
.sumLabel{color:#64748b;font-weight:900}
.sumVal{font-weight:1200}
.primaryBtn{
  border:0;border-radius:14px;padding:12px 14px;
  background:linear-gradient(135deg,#2563eb,#60a5fa);
  color:#fff;font-weight:1100;cursor:pointer;
}
.ghostBtn{
  border:1px solid #e5e7eb;background:#fff;
  border-radius:14px;padding:10px 12px;
  font-weight:1100;cursor:pointer;
}
.ghostBtn:disabled{opacity:.5;cursor:not-allowed}
.note{font-size:12px;color:#64748b;font-weight:800}

@media (max-width:520px){
  .img{width:78px;height:78px}
}
`;
